import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, FlatList, TouchableOpacity, ImageBackground } from 'react-native';
import Colors from '../../../constants/Colorsv2';
import { Text } from '../../../globalComponents/NativeOverrides';
import { BracketProps, EventProps, TournamentProps } from "../../../store/types/sr_svc"
import TournamentCard from './TournamentCard';
import { event_getClosestDay, event_getUniqueDivisions, event_getUniqueSubDivisions } from '../helpers/events';
import { FINALIZED_STATUSES } from '../../../Datav2/events';
import { Icon } from 'react-native-elements';
import { useDispatch, useSelector } from 'react-redux';
import { event_clearScheduleRange, event_getBracketsByLeagueId, event_getEventIdsByDate, event_getTournamentsByLeagueId } from '../../../store/actionsv1/events';
import { CompetitionMatchMarketProps } from '../../../store/types/tp_svc';
import { V1_UPDATE_DEFAULT_LINES_VISIBLE } from '../../../store/actionsv1/types';
import { AdServer, BEEventApi, Components } from 'be-components';
import TeamEventHolder from '../../../Componentsv1/TeamEventHolder';

type LeagueScheduleProps = {
    navigation:any,
    league_id:string,
    default_tournaments?:boolean,
    default_date?: string,
    source: 'competition_create'|'market',
    add_to_comp_toggle?:boolean,
    markets_override?:string[],
    hide_past_events?:boolean,
    hide_tournaments?:boolean,
    onScrollTo?: (offset:number) => void,
    additional_competition_markets?: { event_id:string, competition_match_markets:CompetitionMatchMarketProps[] }[],
    competition_event_ids?:string[],
    onAddToComp?: (event:EventProps) => void,
    onShowAdditionalMarkets?: (event:EventProps) => void
}
const LeagueSchedule = ({ navigation, default_tournaments, league_id, hide_tournaments, hide_past_events, default_date, onScrollTo }:LeagueScheduleProps) => {
    const [ loaded, setLoaded ] = useState(false);
    const [ brackets, setBrackets ] = useState<BracketProps[]>([]);
    const [ futures_visible, setFuturesVisible ] = useState(false);
    const [ live_extended, setLiveExtended ] = useState(false);
    const [ active_date_state, setActiveDateState ] = useState({
        active_date: default_date,
        active_date_index: 0
    });
    const [ selected_time, setSelectedTime ] = useState<string|undefined>(undefined);
    const [ selected_division, setSelectedDivision ] = useState<string|undefined>(undefined)
    const [ selected_subdivision, setSelectedSubDivision ] = useState<string|undefined>(undefined)
    const [ league_schedule_size, setLeagueScheduleSize ] = useState({ width:300, height:0 })

    const { active_date, active_date_index } = active_date_state

    const dateRef = useRef()
    const dispatch = useDispatch();
    const app_size = useSelector((state:any) => state.util.app_size, (left, right) => left.width == right.width);
    const events_dict: { [key:string] :EventProps } = useSelector((state:any) => state.event.events, (left, right) => left == right);
    const events = Object.values(events_dict).filter((e:EventProps) => e.league_id == league_id);
    const player = useSelector((state:any) => state.player.player, (left, right) => left?.player_id == right?.player_id);
    const unique_dates = [ ...new Set(events.map(e => moment(e.scheduled_datetime).format('YYYY-MM-DD'))) ].sort((a,b) => moment(a).unix() - moment(b).unix())
    const tournaments_dict = useSelector((state:any) => state.event.tournaments, (left, right) => left == right);
    const tournaments = Object.values(tournaments_dict).filter((t:TournamentProps) => t.league_id == league_id)
    const default_lines = useSelector((state:any) => state.auth.default_lines, (left, right) => left == right);
    let date_events:EventProps[] = events.filter(e => moment(e.scheduled_datetime).format('YYYY-MM-DD') == active_date)

    let filtered_events = [ ...date_events ]
    if(selected_division){
        if(selected_division === 'Top25'){ 
            filtered_events = filtered_events.filter((e:EventProps) => e.away?.rank || e.home?.rank) 
        } else {
            filtered_events = filtered_events.filter((e:EventProps) => e.away?.division == selected_division || e.home?.division == selected_division)
        }
    }
    if(selected_subdivision){
        filtered_events = filtered_events.filter((e:EventProps) => e.away?.sub_division == selected_subdivision || e.home?.sub_division == selected_subdivision)
    }
    const closed_events = filtered_events.filter(e => FINALIZED_STATUSES.includes(e.status)).sort((a,b) => moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix())
    
    const upcoming_events = useMemo(() => {
        let new_evs = filtered_events.filter(e => !FINALIZED_STATUSES.includes(e.status) && moment().isBefore(moment(e.scheduled_datetime))).sort((a,b) => moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix())        
        return new_evs
    },[events_dict, active_date, selected_division, selected_subdivision])

    const unique_times = [ ...new Set(upcoming_events.map(e => moment(e.scheduled_datetime).format('h:mm a'))) ]
    useEffect(() => {
        setSelectedTime(unique_times[0])
    },[unique_times.length])

    let live_events = filtered_events.filter(e => !FINALIZED_STATUSES.includes(e.status) && moment().isAfter(moment(e.scheduled_datetime)))
    if(!live_extended){
        live_events = live_events.slice(0, 3)
    }
    
    const unique_divisions = event_getUniqueDivisions(date_events)
    const unique_sub_divisions = event_getUniqueSubDivisions(filtered_events)



    useEffect(() => {
        if(!league_id){ return }
        dispatch(event_clearScheduleRange())
        getDataFromServer(moment().format('YYYY/MM/DD'))
    },[league_id])

    useEffect(() => {
        if(loaded || unique_dates.length === 0){ return } //Only do this for the first set of range dates
        let closest_day = default_date
        let active_date_index = unique_dates.findIndex(d => d === default_date)
        if(active_date_index == -1){ 
            closest_day = event_getClosestDay(unique_dates)
            active_date_index = unique_dates.findIndex(d => d === closest_day)
        }
        setActiveDateState({ active_date:closest_day, active_date_index })
        //setLoaded(true)
    },[unique_dates.length])

    useEffect(() => {
        if(!default_tournaments){ return }
        setFuturesVisible(true)
    },[default_tournaments])

    useEffect(() => {
        if(active_date_index === 0){ return }
        if(dateRef?.current){
            dateRef.current.scrollToIndex({ animated: false, index: active_date_index });
        }
    },[active_date_index])


    /*
    const loadMoreIfNeeded = ({ layoutMeasurement, contentOffset, contentSize }:any) => {
        if(postsLoading){ return }
        const paddingToBottom = 300
        const needMore = layoutMeasurement.height + contentOffset.y >= contentSize.height - paddingToBottom
        if(needMore){ setIsScrollAtEnd(true) } else { setIsScrollAtEnd(false) }
    }
    */

    const getDataFromServer = async(date:string) => {
        await dispatch(event_getEventIdsByDate(league_id, date))
        const brackets = await event_getBracketsByLeagueId(league_id)
        setBrackets(brackets)
        if(!hide_tournaments){ dispatch(event_getTournamentsByLeagueId(league_id)) }

    }

    const handleDateChange = (day:string) => {
        let index = unique_dates.findIndex(d => d === day)
        if((index == 0 || index == unique_dates.length - 1)){
            getDataFromServer(moment(day).format('YYYY/MM/DD'))
        }
        setActiveDateState({
            ...active_date_state, active_date: day
        })
    }

    const renderStartTimes = (data: { item:string, index:number }) => {
        //const formatted_time = moment(data.item, 'h:mm a')
        const time_events = filtered_events.filter(e => moment(e.scheduled_datetime).format('h:mm a') == data.item);
        const selected = selected_time == data.item ? true : false;
        return (
            <View>
                <TouchableOpacity style={{ flexDirection:'row', height: 50, alignItems:'center', padding:10, borderBottomWidth:1, borderBottomColor:Colors.shades.shade100 }} onPress={() => {
                    setSelectedTime(selected ? undefined : data.item)
                    if(onScrollTo && !selected){ 
                        setTimeout(() => {
                            onScrollTo(league_schedule_size.height + 35 + (data.index * 50)) 
                        }, 500);
                    }
                    }}>
                    <View style={{ flex:1 }}>
                        <Text size={14} color={Colors.brand.midnight} weight='bold'>{data.item}</Text>
                        <Text size={12} color={Colors.brand.midnight} weight='regular'>{time_events.length} Games</Text>
                    </View>
                    <Components.Icons.ChevronIcon direction={selected ? 'up' : 'down'} color={Colors.brand.midnight} size={8} />
                </TouchableOpacity>
                {selected ?
                <FlatList 
                    key={`${data.item}:events`}
                    data={time_events.map(e => e.event_id)}
                    initialNumToRender={2}
                    maxToRenderPerBatch={10}
                    updateCellsBatchingPeriod={100}
                    windowSize={30}
                    removeClippedSubviews={true}
                    renderItem={renderEvents}
                    keyExtractor={(item, index) => `${item}:${'team'}`}
                />
                :<></>}
            </View>
        )
    }

    const renderDates = (data: {item: string, index:number}) => {
        const formatted_date = moment(data.item)
        const a = data.item === active_date ? true : false
        return (
            <TouchableOpacity style={{ width:75, padding:5, paddingLeft:10, paddingRight:10 }} onPress={() => handleDateChange(data.item)}>
                <Text size={a?14:12} color={a?Colors.brand.midnight:Colors.brand.slate} weight={a?'bold':'regular'} textAlign='center'>{formatted_date.format('ddd')}</Text>
                <Text size={a?14:12} color={a?Colors.brand.midnight:Colors.brand.slate} weight={a?'bold':'regular'} textAlign='center'>{formatted_date.format('MMM D')}</Text>
            </TouchableOpacity>
        )
    }

    const renderEvents = useCallback((data: {item: string, index:number}) => {

        return (
            <View style={{ margin:1 }}>
                <TeamEventHolder
                    event_id={data.item}
                    navigation={navigation}
                />
                {(data.index > 0 && data.index % 5 == 0)?
                <AdServer
                    placement='events_feed'
                    ad_type='discover'
                    device_width={app_size.width}
                    role={player?.type}
                    onViewAd={(e) => BEEventApi.saveEvent(e)}
                    onClickAd={(e) => BEEventApi.saveEvent(e)}
                />
                :<></>}
            </View>
        )
    },[])
    

    const renderTournaments = useCallback((data: {item: string, index:number}) => {
        return (
            <View style={{ margin:8 }}>
                <TournamentCard navigation={navigation} tournament_id={data.item}/>
            </View>
        )
    },[])

    const renderDivisions = (data: { item:string, index:number}) => {
        const selected = selected_division == data.item ? true : false
        return (
            <TouchableOpacity 
                style={{ margin:5, borderRadius:100, borderWidth:1, borderColor:Colors.shades.shade400, backgroundColor:selected?Colors.highlights.highlight200 : Colors.shades.white, padding:10, paddingRight:20, paddingLeft:20 }}
                onPress={() => setSelectedDivision(selected ? undefined : data.item)}>
                <Text size={14} color={selected?Colors.shades.white:Colors.brand.midnight}>{data.item}</Text>
            </TouchableOpacity>
        )
    }

    const renderSubDivisions = (data: { item:string, index:number}) => {
        const selected = selected_subdivision == data.item ? true : false
        return (
            <TouchableOpacity 
                style={{ margin:5, borderRadius:100, borderWidth:1, borderColor:Colors.shades.shade400, backgroundColor:selected?Colors.highlights.highlight200 : Colors.shades.white, padding:10, paddingRight:20, paddingLeft:20 }}
                onPress={() => setSelectedSubDivision(selected ? undefined : data.item)}>
                <Text size={14} color={selected?Colors.shades.white:Colors.brand.midnight}>{data.item}</Text>
            </TouchableOpacity>
        )
    }

    const renderBrackets = (data:{ item:BracketProps, index:number }) => {
        return (
            <TouchableOpacity style={{ padding:10, flexDirection:'row', alignItems:'center', borderBottomColor:Colors.shades.shade600, borderBottomWidth:1 }}
            onPress={() => navigation.navigate('CompetitionStack', { screen: 'BracketPlay', initial:false, bracket_id: data.item.bracket_id })}>
                <View style={{ flex:1 }}>
                    <Text size={14} color={Colors.brand.midnight} weight='bold'>{data.item.bracket_name}</Text>
                    <Text style={{ marginTop:4 }} size={12} color={Colors.brand.slate} weight='regular'>{moment().isBefore(moment(data.item.scheduled_datetime)) ? moment(data.item.scheduled_datetime).format('MM/DD/YYYY hh:mm a') : 'In progress'}</Text>
                </View>
                <Icon style={{ padding:5 }} name='chevron-right' color={Colors.brand.midnight} size={16} />
            </TouchableOpacity>
        )
    }


    return (
        <View >
            <View nativeID='header_stuff' onLayout={(ev) => {
                const { width, height } = ev.nativeEvent.layout;
                setLeagueScheduleSize({ width, height })
            }}>

            {!hide_tournaments && tournaments.length > 0 ?
            <View nativeID='upcoming-events' style={{ marginBottom:10 }}>
                <View style={{ backgroundColor:Colors.shades.shade100, flexDirection:'row', alignItems:'center', justifyContent:'space-between' }}>
                    <ImageBackground 
                        style={{ flexDirection:'row', width:league_schedule_size.width*0.75 }} 
                        source={{ uri: require('../../../assets/images/diagnal_header.png') }}
                        resizeMode='cover'>
                        <Text size={16} weight='semibold' color={Colors.shades.white} style={{margin:15}}>Upcoming Events / Futures</Text>
                    </ImageBackground>
                    <TouchableOpacity style={{ minWidth:40, padding:10 }} onPress={() => setFuturesVisible(!futures_visible)}>
                        <Icon name={futures_visible?'chevron-up':'chevron-down'} type='feather' color={Colors.brand.midnight} />
                    </TouchableOpacity>
                </View>
                {futures_visible ? 
                <FlatList 
                    key='league_tournaments'
                    data={tournaments.filter(t => ['scheduled','created','active','inprogress'].includes(t.status)).sort((a,b) => moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix()).map(t => t?.tournament_id)} 
                    renderItem={renderTournaments} 
                    keyExtractor={(item, index) => `${item}:${index}`} />
                :<></>}
            </View>
            :<></>}
            {brackets.length > 0 ?
            <View nativeID='upcoming-events' style={{ marginBottom:10 }}>
                <View style={{ backgroundColor:Colors.shades.shade100, flexDirection:'row', alignItems:'center', justifyContent:'space-between' }}>
                    <ImageBackground 
                        style={{ flexDirection:'row', width:league_schedule_size.width*0.75 }} 
                        source={{ uri: require('../../../assets/images/diagnal_header.png') }}
                        resizeMode='cover'>
                        <Text size={16} weight='semibold' color={Colors.shades.white} style={{margin:15}}>Brackets</Text>
                    </ImageBackground>
                </View>
                <FlatList 
                    key='league_brackets'
                    data={brackets} 
                    renderItem={renderBrackets} 
                    keyExtractor={(item, index) => `${item}:${index}`} />
            </View>
            :<></>}
            {events.length > 0 ?
            <View nativeID='event_schedule_dates'>
                <FlatList 
                    key={'league_dates'}
                    ref={dateRef}
                    data={unique_dates} 
                    renderItem={renderDates} 
                    keyExtractor={(item, index) => `${item}:${index}`} 
                    horizontal 
                    showsHorizontalScrollIndicator={false}
                    initialScrollIndex={active_date_index} 
                    getItemLayout={(data, index) => ({length: 75, offset: 75 * index, index})}
                    />
                    {unique_divisions.length > 1 ?
                    <View nativeID='event_divisions' style={{ backgroundColor:Colors.shades.shade100 }}>
                        <FlatList 
                            key='divisions'
                            horizontal 
                            data={unique_divisions} 
                            renderItem={renderDivisions} 
                            keyExtractor={(item, index) => `${item}:${index}`}/>
                    </View>
                    :<></>}
                    {selected_division && unique_sub_divisions[0] ?
                    <View nativeID='event_divisions' style={{ backgroundColor:Colors.shades.shade100 }}>
                        <FlatList 
                            key='sub_divisions'
                            horizontal 
                            data={unique_sub_divisions} 
                            renderItem={renderSubDivisions} 
                            keyExtractor={(item, index) => `${item}:${index}`}/>
                    </View>
                    :<></>}
            </View>
            
            :<></>}
            {live_events.length > 0 && !hide_past_events ? 
            <View nativeID='live-events'>
                 <ImageBackground style={{ flexDirection:'row', alignItems:'center', justifyContent:'space-between', width:league_schedule_size.width }} source={require('../../../assets/images/diagnal_callout.png')}>
                    <Text size={16} weight='semibold' color={Colors.shades.white} style={{margin:15}}>Live Games</Text>
                    <View style={{flexDirection:'row', alignItems:'center', marginRight:15}}>
                        <Icon name='zap' type='feather' color={Colors.shades.white} size={16} />
                        <Text size={16} weight='semibold' color={Colors.shades.white} style={{marginLeft:5}}>LIVE</Text>
                    </View>
                </ImageBackground>
                <FlatList 
                    key='live_events'
                    data={live_events.map(e => e.event_id)} 
                    renderItem={renderEvents} 
                    keyExtractor={(item,index) => `${item}:${index}`} />
                <TouchableOpacity style={{margin:20, padding:10, backgroundColor:Colors.shades.shade100, borderRadius:100}} onPress={() => setLiveExtended(!live_extended)}>
                    <Text size={14} color={Colors.brand.electric} weight='semibold' textAlign='center'>{live_extended?'See Less':'See More'}</Text>
                </TouchableOpacity>
            </View>
            :<></>}
            </View>
            {upcoming_events.length > 0 ?
            <View nativeID='upcoming-events'>
                <View style={{ backgroundColor:Colors.shades.shade100, flexDirection:'row', alignItems:'center', justifyContent:'space-between' }}>
                    <ImageBackground 
                        style={{ flexDirection:'row', width:league_schedule_size.width*0.75 }} 
                        source={{ uri: require('../../../assets/images/diagnal_header.png')}}
                        resizeMode='cover'
                        >
                        <Text size={16} weight='semibold' color={Colors.shades.white} style={{margin:15}}>Upcoming Games</Text>
                    </ImageBackground>
                    <TouchableOpacity style={{ padding:5, paddingRight:10, justifyContent:'center', alignItems:'center' }}
                    onPress={() => dispatch({ type:V1_UPDATE_DEFAULT_LINES_VISIBLE, default_lines_visible: true })}>
                        <Text style={{ marginBottom:2 }} size={12} color={Colors.brand.electric} weight='regular'>Showing</Text>
                        {default_lines == 'best_available' ?
                            <Text size={12} color={Colors.brand.electric} weight='semibold'>Best Available</Text>
                        :
                            <Text size={12} color={Colors.brand.electric} weight='semibold'>Last Trade</Text>
                        }
                    </TouchableOpacity>
                </View>
                <View>
                {upcoming_events.length > 30 ?
                    <FlatList
                        key={'event_times'}
                        data={unique_times}
                        renderItem={renderStartTimes}
                        keyExtractor={(item) => item}
                    />
                    :
                    <FlatList 
                        key={`all_upcoming_events`}
                        data={upcoming_events.map(e => e.event_id)}
                        initialNumToRender={2}
                        maxToRenderPerBatch={10}
                        updateCellsBatchingPeriod={100}
                        windowSize={30}
                        removeClippedSubviews={true}
                        renderItem={renderEvents}
                        keyExtractor={(item, index) => `${item}:${'team'}`}
                    />
                    }
                </View>
            </View>
            :<></>}
            {closed_events.length > 0 && !hide_past_events ?
            <View nativeID='closed-events'>
                <View style={{ backgroundColor:Colors.brand.midnight }}>
                    <Text size={16} weight='semibold' color={Colors.shades.white} style={{margin:15}}>Closed Games</Text>
                </View>
                <FlatList 
                    key='league_closed_events'
                    data={closed_events.map(e => e.event_id)} 
                    renderItem={renderEvents} 
                    keyExtractor={(item, index) => `${item}:${index}`} />
            </View>
            :<></>}
        </View>
    )
}

export default LeagueSchedule